import React, { useCallback, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  NodeTypes,
  EdgeTypes,
  ReactFlowProvider,
  useReactFlow,
  Panel,
  getNodesBounds,
} from 'reactflow';
import { toPng, toSvg } from 'html-to-image';
import 'reactflow/dist/style.css';

import CompanyNode from './nodes/CompanyNode';
import FloatingEdge from './edges/FloatingEdge';
import { CompanyNode as CompanyNodeType, LayoutDirection, NodeCustomization, PercentageDisplayMode } from '../types';
import { layoutGraph } from '../utils/layoutEngine';
import { legacyLayout } from '../utils/legacyLayout';
import { ClusterBounds } from '../utils/layoutEngine.types';
import ClusterBackground from './ClusterBackground';

export interface OrganizationChartRef {
  exportToPNG: (fullChart?: boolean) => Promise<void>;
  exportToSVG: (fullChart?: boolean) => Promise<void>;
  fitView: () => void;
  resetLayout: () => void;
  getNodePositions: () => Record<string, { x: number; y: number }>;
  setNodePositions: (positions: Record<string, { x: number; y: number }>) => void;
  focusNode: (nodeId: string) => void;
  zoomIn: () => void;
  zoomOut: () => void;
}

interface OrganizationChartProps {
  companyNodes: CompanyNodeType[];
  selectedNodeId: string | null;
  selectedNodeIds?: string[];
  onNodeClick: (nodeId: string) => void;
  onToggleExpand: (nodeId: string) => void;
  onToggleHide: (nodeId: string) => void;
  onCustomizeNode: (nodeId: string, customization: NodeCustomization) => void;
  onIsolateTree: (nodeId: string) => void;
  onSelectionChange?: (params: { nodes: Array<{ id: string }> }) => void;
  onPaneClick?: () => void;
  layout: LayoutDirection;
  maxDepth: number | 'all';
  showCountryFlags: boolean;
  percentageDisplayMode: PercentageDisplayMode;
  showClusterGrouping?: boolean;
  onNodesDragEnd?: () => void;
  showGrid?: boolean;
  showSingleShareholder?: boolean;
  showDirectors?: boolean;
}

const nodeTypes: NodeTypes = {
  company: CompanyNode,
};

const edgeTypes: EdgeTypes = {
  floating: FloatingEdge,
};

const OrganizationChartInner = forwardRef<OrganizationChartRef, OrganizationChartProps>(({
  companyNodes,
  selectedNodeId,
  selectedNodeIds: _selectedNodeIds = [],
  onNodeClick,
  onToggleExpand,
  onToggleHide,
  onCustomizeNode,
  onIsolateTree,
  onSelectionChange,
  onPaneClick,
  layout,
  maxDepth,
  showCountryFlags,
  percentageDisplayMode,
  showClusterGrouping = true,
  onNodesDragEnd,
  showGrid = true,
  showSingleShareholder = true,
  showDirectors = true
}, ref) => {
  const { fitView, getNodes, setCenter, zoomIn: rfZoomIn, zoomOut: rfZoomOut } = useReactFlow();
  const [openMenuNodeId, setOpenMenuNodeId] = React.useState<string | null>(null);
  const [shouldApplyLayout, setShouldApplyLayout] = React.useState(true);
  const [clusters, setClusters] = React.useState<ClusterBounds[]>([]);
  const previousFilteredNodesRef = React.useRef<typeof filteredNodes>([]);
  const previousLayoutRef = React.useRef<LayoutDirection>(layout);
  
  // Filter nodes by depth, expansion state, and visibility
  const filteredNodes = useMemo(() => {
    // First filter by depth
    let nodes = maxDepth === 'all' ? companyNodes : companyNodes.filter(node => 
      node.depth < maxDepth || node.country === 'EXT'
    );
    
    // Filter out hidden nodes and their children
    const hiddenNodeIds = new Set<string>();
    const getHiddenDescendants = (nodeId: string) => {
      hiddenNodeIds.add(nodeId);
      const children = nodes.filter(n => n.parentId === nodeId);
      children.forEach(child => getHiddenDescendants(child.id));
    };
    
    // Mark hidden nodes and their descendants (including external shareholders when explicitly hidden)
    nodes.filter(n => n.isHidden).forEach(n => getHiddenDescendants(n.id));
    
    // Filter out collapsed children
    const collapsedNodeIds = new Set<string>();
    const getCollapsedDescendants = (nodeId: string) => {
      const children = nodes.filter(n => n.parentId === nodeId);
      children.forEach(child => {
        collapsedNodeIds.add(child.id);
        getCollapsedDescendants(child.id);
      });
    };
    
    // Mark collapsed children (external shareholders cannot be collapsed since they have no children)
    nodes.filter(n => !n.isExpanded && n.country !== 'EXT').forEach(n => getCollapsedDescendants(n.id));
    
    // Return visible nodes only
    return nodes.filter(n => !hiddenNodeIds.has(n.id) && !collapsedNodeIds.has(n.id));
  }, [companyNodes, maxDepth]);

  // Get ownership path for highlighting (including all parents)
  const getOwnershipPath = useCallback((nodeId: string): string[] => {
    const pathIds = new Set<string>();
    const visited = new Set<string>();
    
    const addNodeAndParents = (currentId: string) => {
      if (visited.has(currentId)) return; // Prevent infinite loops
      visited.add(currentId);
      
      pathIds.add(currentId);
      const currentNode = filteredNodes.find(n => n.id === currentId);
      
      if (currentNode) {
        // Add all parents (multiple parent support)
        if (currentNode.parentIds && currentNode.parentIds.length > 0) {
          currentNode.parentIds.forEach(parentId => {
            addNodeAndParents(parentId);
          });
        } else if (currentNode.parentId) {
          // Fallback to single parent
          addNodeAndParents(currentNode.parentId);
        }
      }
    };
    
    addNodeAndParents(nodeId);
    const result = Array.from(pathIds);
    console.log(`Ownership path for ${nodeId}:`, result);
    return result;
  }, [filteredNodes]);

  // Count separate trees
  const treeCount = useMemo(() => {
    const rootNodes = filteredNodes.filter(node => 
      !filteredNodes.some(n => n.id === node.parentId)
    );
    return rootNodes.length;
  }, [filteredNodes]);

  // Convert company nodes to React Flow nodes
  const flowNodes: Node[] = useMemo(() => {
    const ownershipPath = selectedNodeId ? getOwnershipPath(selectedNodeId) : [];
    
    return filteredNodes.map(node => {
      // Check if node has children
      const hasChildren = companyNodes.some(n => n.parentId === node.id);
      
      // Enhanced highlighting logic: node is in ownership path if it's the selected node OR any of its parents
      const isInOwnershipPath = selectedNodeId && ownershipPath.includes(node.id) && selectedNodeId !== node.id;
      const isSelected = selectedNodeId === node.id;
      
      if (selectedNodeId && (isSelected || isInOwnershipPath)) {
        console.log(`Node ${node.name} (${node.id}) - Selected: ${isSelected}, InPath: ${isInOwnershipPath}`);
      }
      
      return {
        id: node.id,
        type: 'company',
        position: { x: 0, y: 0 }, // Will be set by layout
        zIndex: openMenuNodeId === node.id ? 10000 : undefined,
        data: {
          id: node.id,
          name: node.name,
          country: node.country,
          directors: node.directors,
          shareholders: node.shareholders,
          ownershipInfo: node.ownershipInfo,
          isVirtualRoot: node.isVirtualRoot,
          isSelected: isSelected,
          isInOwnershipPath: isInOwnershipPath,
          isExpanded: node.isExpanded,
          hasChildren: hasChildren,
          customization: node.customization,
          showCountryFlags: showCountryFlags,
          percentageDisplayMode: percentageDisplayMode,
          showSingleShareholder,
          showDirectors,
          onToggleExpand: onToggleExpand,
          onToggleHide: onToggleHide,
          onCustomizeNode: onCustomizeNode,
          onIsolateTree: onIsolateTree,
          onHighlightPath: onNodeClick,
          isMenuOpen: openMenuNodeId === node.id,
          onMenuToggle: () => setOpenMenuNodeId(openMenuNodeId === node.id ? null : node.id)
        },
        draggable: true,
      };
    });
  }, [filteredNodes, selectedNodeId, getOwnershipPath, companyNodes, onToggleExpand, onToggleHide, onCustomizeNode, onIsolateTree, onNodeClick, showCountryFlags, percentageDisplayMode, openMenuNodeId, showSingleShareholder, showDirectors]);

  // Convert company nodes to React Flow edges (supporting multiple parents)
  const flowEdges: Edge[] = useMemo(() => {
    const ownershipPath = selectedNodeId ? getOwnershipPath(selectedNodeId) : [];
    const edges: Edge[] = [];
    
    // Debug ownership path highlighting
    if (selectedNodeId) {
      console.log('Selected node:', selectedNodeId, 'Ownership path:', ownershipPath);
    }
    
    // Helper to get shareholder percentage for an edge
    const getShareholderPercentage = (childNode: CompanyNodeType, parentId: string): number | null => {
      const shareholder = childNode.shareholders?.find(sh => {
        // Match by parent company name
        const parentNode = filteredNodes.find(n => n.id === parentId);
        return parentNode && sh.name === parentNode.name;
      });
      
      if (shareholder && shareholder.percentage < 100) {
        return shareholder.percentage;
      }
      return null;
    };
    
    filteredNodes.forEach(node => {
      // Create edges for all parent connections
      if (node.parentIds && node.parentIds.length > 0) {
        node.parentIds.forEach(parentId => {
          // Check if parent is visible
          const parentExists = filteredNodes.some(n => n.id === parentId);
          
          if (parentExists) {
            // Enhanced highlighting: check if either the node or parent is in the ownership path
            const isHighlighted = selectedNodeId && (
              (ownershipPath.includes(node.id) && ownershipPath.includes(parentId)) ||
              (selectedNodeId === node.id && ownershipPath.includes(parentId)) ||
              (selectedNodeId === parentId && ownershipPath.includes(node.id))
            );

            // Get percentage for label
            const percentage = getShareholderPercentage(node, parentId);
            const showLabel = (percentageDisplayMode === 'line' || percentageDisplayMode === 'both') && percentage !== null;

            const edge: Edge = {
              id: `${parentId}-${node.id}`,
              source: parentId,
              target: node.id,
              type: 'floating',
              animated: isHighlighted ? true : false,
              style: {
                stroke: isHighlighted ? '#ffd700' : '#6b7280',
                strokeWidth: isHighlighted ? 4 : 2,
                strokeOpacity: isHighlighted ? 1 : 0.5,
              },
              label: showLabel ? `${percentage}%` : undefined,
              labelStyle: showLabel ? { 
                fill: '#333', 
                fontWeight: 600,
                fontSize: '12px',
                backgroundColor: '#fff',
                padding: '2px 6px',
                borderRadius: '3px',
                border: '1px solid #ddd'
              } : undefined,
              labelBgStyle: showLabel ? { 
                fill: '#fff',
                fillOpacity: 0.9
              } : undefined,
              labelBgPadding: showLabel ? [4, 4] as [number, number] : undefined,
              labelBgBorderRadius: showLabel ? 3 : undefined,
            };
            
            if (isHighlighted) {
              console.log('Highlighting edge:', edge);
            }
            
            edges.push(edge);
          }
        });
      } else if (node.parentId) {
        // Fallback to single parent for backward compatibility
        const parentExists = filteredNodes.some(n => n.id === node.parentId);
        
        if (parentExists) {
          // Enhanced highlighting: check if either the node or parent is in the ownership path
          const isHighlighted = selectedNodeId && (
            (ownershipPath.includes(node.id) && ownershipPath.includes(node.parentId!)) ||
            (selectedNodeId === node.id && ownershipPath.includes(node.parentId!)) ||
            (selectedNodeId === node.parentId && ownershipPath.includes(node.id))
          );

          // Get percentage for label
          const percentage = getShareholderPercentage(node, node.parentId);
          const showLabel = (percentageDisplayMode === 'line' || percentageDisplayMode === 'both') && percentage !== null;

          const edge: Edge = {
            id: `${node.parentId}-${node.id}`,
            source: node.parentId,
            target: node.id,
            type: 'floating',
            animated: isHighlighted ? true : false,
            style: {
              stroke: isHighlighted ? '#ffd700' : '#6b7280',
              strokeWidth: isHighlighted ? 4 : 2,
              strokeOpacity: isHighlighted ? 1 : 0.5,
            },
            label: showLabel ? `${percentage}%` : undefined,
            labelStyle: showLabel ? { 
              fill: '#333', 
              fontWeight: 600,
              fontSize: '12px'
            } : undefined,
            labelBgStyle: showLabel ? { 
              fill: '#fff',
              fillOpacity: 0.9
            } : undefined,
            labelBgPadding: showLabel ? [4, 4] as [number, number] : undefined,
            labelBgBorderRadius: showLabel ? 3 : undefined,
          };
          
          if (isHighlighted) {
            console.log('Highlighting single parent edge:', edge);
          }
          
          edges.push(edge);
        }
      }
    });
    
    return edges;
  }, [filteredNodes, selectedNodeId, getOwnershipPath, percentageDisplayMode]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Track when filtered nodes change (excluding menu state changes)
  useEffect(() => {
    const prev = previousFilteredNodesRef.current;
    const curr = filteredNodes;

    let isStructural = prev.length !== curr.length;
    if (!isStructural) {
      const prevIds = new Set(prev.map(n => n.id));
      isStructural = curr.some(n => !prevIds.has(n.id));
    }
    if (!isStructural) {
      const prevMap = new Map(prev.map(n => [n.id, n]));
      isStructural = curr.some(n => {
        const p = prevMap.get(n.id);
        if (!p) return true;
        if (p.parentId !== n.parentId) return true;
        const prevParents = JSON.stringify(p.parentIds || []);
        const currParents = JSON.stringify(n.parentIds || []);
        if (prevParents !== currParents) return true;
        if ((p.shareholders?.length || 0) !== (n.shareholders?.length || 0)) return true;
        // Compare shareholder names (positions matter for parent-link stability)
        const prevShNames = (p.shareholders || []).map(s => s.name).join('|');
        const currShNames = (n.shareholders || []).map(s => s.name).join('|');
        if (prevShNames !== currShNames) return true;
        return false;
      });
    }

    const layoutChanged = previousLayoutRef.current !== layout;

    if (isStructural || layoutChanged || prev.length === 0) {
      setShouldApplyLayout(true);
      previousFilteredNodesRef.current = curr;
      previousLayoutRef.current = layout;
    } else {
      // Data-only update — refresh ref but skip relayout
      previousFilteredNodesRef.current = curr;
    }
  }, [filteredNodes, layout]);

  // Forceer re-layout wanneer showClusterGrouping toggle wijzigt — clusters
  // worden alleen berekend tijdens een volledige layoutGraph call, niet bij
  // data-only updates. Zonder deze trigger blijft de oude clusters-state
  // hangen na een settings-toggle.
  const previousClusterGroupingRef = React.useRef<boolean | undefined>(showClusterGrouping);
  useEffect(() => {
    if (previousClusterGroupingRef.current !== showClusterGrouping) {
      previousClusterGroupingRef.current = showClusterGrouping;
      setShouldApplyLayout(true);
    }
  }, [showClusterGrouping]);

  // Apply layout when data changes
  useEffect(() => {
    let cancelled = false;
    if (flowNodes.length === 0) return;

    if (shouldApplyLayout) {
      (async () => {
        try {
          const result = await layoutGraph(flowNodes, flowEdges, layout, {
            groupByRoot: showClusterGrouping ?? true,
          });
          if (cancelled) return;
          setNodes(result.nodes);
          setEdges(result.edges);
          setClusters(result.clusters);
          setShouldApplyLayout(false);
        } catch (err) {
          console.warn('ELK layout failed, falling back to legacy:', err);
          if (cancelled) return;
          const fallback = legacyLayout(flowNodes, flowEdges, layout);
          setNodes(fallback.nodes);
          setEdges(fallback.edges);
          setClusters([]);
          setShouldApplyLayout(false);
        }
      })();
    } else {
      const currentNodes = getNodes();
      const updatedNodes = flowNodes.map(flowNode => {
        const existingNode = currentNodes.find(n => n.id === flowNode.id);
        return existingNode ? { ...flowNode, position: existingNode.position } : flowNode;
      });
      setNodes(updatedNodes);
      setEdges(flowEdges);
    }
    return () => { cancelled = true; };
  }, [flowNodes, flowEdges, layout, setNodes, setEdges, shouldApplyLayout, getNodes, showClusterGrouping]);

  // Fit view only when new data is loaded (not on selection changes)
  useEffect(() => {
    if (companyNodes.length > 0 && !selectedNodeId) {
      // Only fit view when data is first loaded (no selection)
      setTimeout(() => {
        fitView({ padding: 0.05, duration: 800 });
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyNodes.length, fitView]); // selectedNodeId intentionally excluded to prevent refitting on selection

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, _node: Node) => {
      // Node click is now handled within CompanyNode component
      // This prevents the default ReactFlow node click behavior
    },
    []
  );

  // Debounce timer for drag end
  const dragEndTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Handle node drag end to trigger undo/redo snapshot (debounced)
  const handleNodeDragStop = useCallback(() => {
    // Clear any existing timer
    if (dragEndTimerRef.current) {
      clearTimeout(dragEndTimerRef.current);
    }
    
    // Set a new timer - wait 300ms after last drag stop
    dragEndTimerRef.current = setTimeout(() => {
      if (onNodesDragEnd) {
        onNodesDragEnd();
      }
    }, 300);
  }, [onNodesDragEnd]);

  // Expose export functions via ref
  useImperativeHandle(ref, () => {
    // Filter voor html-to-image: weer UI-chrome (controls, minimap, grid)
    // uit de export. Behoud alleen de chart-content (nodes + edges).
    const exportFilter = (node: HTMLElement): boolean => {
      if (!(node instanceof Element)) return true;
      const cls = node.className;
      // className kan ook SVGAnimatedString zijn voor SVG-elementen
      const classStr = typeof cls === 'string' ? cls : (cls as any)?.baseVal || '';
      if (
        classStr.includes('react-flow__controls') ||
        classStr.includes('react-flow__minimap') ||
        classStr.includes('react-flow__background') ||
        classStr.includes('react-flow__panel') ||
        classStr.includes('react-flow__attribution')
      ) {
        return false;
      }
      return true;
    };

    return ({
    exportToPNG: async (fullChart: boolean = false) => {
      try {
        const viewportElement = document.querySelector('.react-flow__viewport') as HTMLElement;
        if (!viewportElement) throw new Error('React Flow viewport element not found');

        if (fullChart) {
          const nodes = getNodes();
          const bounds = getNodesBounds(nodes);
          const padding = 20;
          const imageWidth = bounds.width + padding * 2;
          const imageHeight = bounds.height + padding * 2;

          const dataUrl = await toPng(viewportElement, {
            backgroundColor: '#ffffff',
            cacheBust: true,
            pixelRatio: 2,
            width: imageWidth,
            height: imageHeight,
            filter: exportFilter,
            style: {
              width: `${imageWidth}px`,
              height: `${imageHeight}px`,
              transform: `translate(${-bounds.x + padding}px, ${-bounds.y + padding}px) scale(1)`,
            },
          });

          const link = document.createElement('a');
          link.download = `organization-chart-${new Date().toISOString().split('T')[0]}.png`;
          link.href = dataUrl;
          link.click();
        } else {
          const dataUrl = await toPng(viewportElement, {
            backgroundColor: '#ffffff',
            cacheBust: true,
            pixelRatio: 2,
            filter: exportFilter,
          });

          const link = document.createElement('a');
          link.download = `organization-chart-${new Date().toISOString().split('T')[0]}.png`;
          link.href = dataUrl;
          link.click();
        }
      } catch (error) {
        console.error('Error exporting PNG:', error);
        throw error;
      }
    },
    exportToSVG: async (fullChart: boolean = false) => {
      try {
        const viewportElement = document.querySelector('.react-flow__viewport') as HTMLElement;
        if (!viewportElement) throw new Error('React Flow viewport element not found');

        if (fullChart) {
          const nodes = getNodes();
          const bounds = getNodesBounds(nodes);
          const padding = 20;
          const imageWidth = bounds.width + padding * 2;
          const imageHeight = bounds.height + padding * 2;

          const pngDataUrl = await toPng(viewportElement, {
            backgroundColor: '#ffffff',
            cacheBust: true,
            pixelRatio: 2,
            width: imageWidth,
            height: imageHeight,
            filter: exportFilter,
            style: {
              width: `${imageWidth}px`,
              height: `${imageHeight}px`,
              transform: `translate(${-bounds.x + padding}px, ${-bounds.y + padding}px) scale(1)`,
            },
          });

          const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${imageWidth}" height="${imageHeight}" viewBox="0 0 ${imageWidth} ${imageHeight}">
  <image width="${imageWidth}" height="${imageHeight}" href="${pngDataUrl}"/>
</svg>`;
          const blob = new Blob([svgContent], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.download = `organization-chart-${new Date().toISOString().split('T')[0]}.svg`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
        } else {
          const dataUrl = await toSvg(viewportElement, {
            backgroundColor: '#ffffff',
            cacheBust: true,
            filter: exportFilter,
          });

          const link = document.createElement('a');
          link.download = `organization-chart-${new Date().toISOString().split('T')[0]}.svg`;
          link.href = dataUrl;
          link.click();
        }
      } catch (error) {
        console.error('Error exporting SVG:', error);
        throw error;
      }
    },
    fitView: () => {
      fitView({ padding: 0.05, duration: 800 });
    },
    resetLayout: () => {
      setShouldApplyLayout(true);
      setTimeout(() => {
        fitView({ padding: 0.05, duration: 800 });
      }, 100);
    },
    getNodePositions: () => {
      const nodes = getNodes();
      const positions: Record<string, { x: number; y: number }> = {};
      nodes.forEach(node => {
        positions[node.id] = { x: node.position.x, y: node.position.y };
      });
      return positions;
    },
    setNodePositions: (positions: Record<string, { x: number; y: number }>) => {
      setNodes(currentNodes =>
        currentNodes.map(node => {
          const savedPosition = positions[node.id];
          if (savedPosition) {
            return { ...node, position: savedPosition };
          }
          return node;
        })
      );
    },
    focusNode: (nodeId: string) => {
      const node = getNodes().find(n => n.id === nodeId);
      if (node) {
        const x = node.position.x + (node.width ?? 220) / 2;
        const y = node.position.y + (node.height ?? 100) / 2;
        setCenter(x, y, { zoom: 1, duration: 800 });
      }
    },
    zoomIn: () => {
      rfZoomIn({ duration: 300 });
    },
    zoomOut: () => {
      rfZoomOut({ duration: 300 });
    },
    });
  }, [fitView, getNodes, setNodes, setCenter, rfZoomIn, rfZoomOut]);

  return (
    <div className="organization-chart" style={{ width: '100%', height: '100%', padding: '0' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onNodeDragStop={handleNodeDragStop}
        onSelectionChange={onSelectionChange}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitViewOptions={{ padding: 0.05 }}
        minZoom={0.1}
        maxZoom={2}
        snapToGrid
        snapGrid={[5, 5]}
        selectionOnDrag
        multiSelectionKeyCode="Shift"
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        proOptions={{ hideAttribution: true }}
      >
        <ClusterBackground clusters={clusters} />
        <Controls />
        <MiniMap 
          nodeColor={(node) => {
            if (node.data?.isSelected) return '#ffd700';
            if (node.data?.isInOwnershipPath) return '#ffed4e';
            if (node.data?.country === 'EXT') return '#9ca3af';
            if (node.data?.isVirtualRoot) return '#2196f3';
            const isRootNode = !node.data?.shareholders || node.data?.shareholders.length === 0 || 
                              node.data?.shareholders.every((s: any) => s.isExternal);
            if (isRootNode) return '#3b82f6';
            return '#f8f9fa';
          }}
          nodeStrokeWidth={1}
          zoomable
          pannable
        />
        {showGrid && <Background variant={BackgroundVariant.Lines} gap={20} color="#cbd5e1" />}

        <Panel position="top-left">
          <div className="chart-info">
            {filteredNodes.length} bedrijven
            {maxDepth !== 'all' && ` (niveaus 0-${maxDepth - 1})`}
            {treeCount > 1 && ` • ${treeCount} aparte bomen`}
          </div>
        </Panel>
      </ReactFlow>
    </div>
  );
});

OrganizationChartInner.displayName = 'OrganizationChartInner';

const OrganizationChart = forwardRef<OrganizationChartRef, OrganizationChartProps>((props, ref) => {
  return (
    <ReactFlowProvider>
      <OrganizationChartInner {...props} ref={ref} />
    </ReactFlowProvider>
  );
});

OrganizationChart.displayName = 'OrganizationChart';

export default OrganizationChart;
